import { getQueryString } from '../../url-provider';
import {getFromStorage, saveToStorage, LOCAL_STORAGE} from '../../../DAL/storage-provider';
import { IUtm, IUtmUrl } from './utm-interfaces';
import { differenceInDays } from 'date-fns'

const LS_STORAGE_KEY = 'utm_ls_object';
const SANITIZE_PATTERN = '[^a-zA-Z0-9-_]+';

/**
 * every UTM property in query string should be sanitized with the regex a-zA-Z0-9-_
 * before saving to the local storage
 */
const sanitize = (text: string) => {
    text = text ?? '';
    return text.replace(new RegExp(SANITIZE_PATTERN, 'ig'), '').trim();
};

/**
 * check if current query string has any of the UTM properties
 * url example: https://dev13.waves.com/plugins/element?utm_source=redirect&utm_medium=referral&utm_campaign=element2#image
 */
const isUtmQueryString = (urmUrl: IUtmUrl) => {
    return (!!urmUrl.utm_source && !!urmUrl.utm_campaign && !!urmUrl.utm_medium) || (!!urmUrl.GCLID && !!urmUrl.w_campaign);
};

/**
 * expiration settings in days, defined in HTML
 * it's used for updating the 'first' UTM fields
 */
const getExpirationSettings = () => {
  const $box = document.getElementById('utm-expires-days');
  if(!$box || !$box.textContent) return 0;
  return Number($box.textContent.trim());
};

/**
 * function applies same timezone for specific date
 */
const treatAsUTC = (date: Date) => {
    const result = new Date(date);
    result.setMinutes(result.getMinutes() - result.getTimezoneOffset());
    return result;
}

/**
 * first is overridden if timestamp of it in LS is greater than (today - setting). ( id="utm-expires-days")
 */
export const isUTMExpired = (utm: IUtm, now: Date) => {
    const expirationDays = getExpirationSettings();
    const daysBetween = differenceInDays(treatAsUTC(now), treatAsUTC(utm.firstTimestamp));
    return daysBetween > expirationDays;
};

/**
 * get UTM from local storage;
 * used in message gears forms, create account, and other similar places
 */
export const getUTMFromStorage = (now: Date = new Date()) : IUtm|null => {

    // try to bring the UTM object from local storage
    const storageData = getFromStorage(LOCAL_STORAGE, LS_STORAGE_KEY);
    if(!storageData) return null;

    let utm: IUtm|null = null;

    try {
        utm = JSON.parse(storageData) as IUtm | null;

        if(utm){
            utm.firstTimestamp = utm.firstTimestamp ? new Date(utm.firstTimestamp) : new Date();
            utm.lastTimestamp = utm.lastTimestamp ? new Date(utm.lastTimestamp) : new Date();
        }

    } catch (ex) {}

    if(!utm) return null;

    // first is overridden if timestamp of it in LS is greater than (today - setting). ( id="utm-expires-days")
    const isExpired = isUTMExpired(utm, now);
    if(isExpired) return null;

    return utm;
};

/**
 * init UTMs on every page load
 * Bring them from query string and apply the logic described in the below JIRA
 * https://wavesaudio.atlassian.net/browse/DSWEB-1838
 */
export const initUTMs = () => {
    const queryString = getQueryString(window.location.search, false);

    const urmUrl: IUtmUrl = {
        utm_source: sanitize(queryString['utm_source']),
        utm_medium: sanitize(queryString['utm_medium']),
        utm_campaign: sanitize(queryString['utm_campaign']),
        GCLID: queryString['gclid'],
        w_campaign: queryString['w_campaign'],
    };

    // check if query string is valid
    if(!isUtmQueryString(urmUrl)) return;

    // if querystring GCLID and w_campaign exist in URL,
    // w_campaign goes to utm_campaign in LS and utm_medium=cpc and utm_source=google
    if(urmUrl.GCLID && urmUrl.w_campaign){
        urmUrl.utm_source = 'google';
        urmUrl.utm_medium = 'cpc';
        urmUrl.utm_campaign = urmUrl.w_campaign;
    }

    const now = new Date();

    // try to bring the UTM object from local storage
    let utm: IUtm|null = getUTMFromStorage(now);

    if(!utm){
        // init urm object first time from the query string
        // write the same UTMs from URL to both first and last
        utm = {
            firstSource: urmUrl.utm_source,
            firstMedium: urmUrl.utm_medium,
            firstCampaign: urmUrl.utm_campaign,
            firstTimestamp: now,

            lastSource: urmUrl.utm_source,
            lastMedium: urmUrl.utm_medium,
            lastCampaign: urmUrl.utm_campaign,
            lastTimestamp: now,
        }
    }
    else{
        // the 'last' properties should always be overwritten
        utm.lastSource = urmUrl.utm_source;
        utm.lastMedium = urmUrl.utm_medium;
        utm.lastCampaign = urmUrl.utm_campaign;
        utm.lastTimestamp = now;
    }

    // save UTMs back to local storage
    utm.firstTimestamp.toJSON();
    utm.lastTimestamp.toJSON();
    saveToStorage(LOCAL_STORAGE, LS_STORAGE_KEY, utm);
};
